import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { GameSliceState, GameState, Difficulty, ObjectiveData } from '../types';

/**
 * Redux slice for game state management
 */

const initialState: GameSliceState = {
  gameState: 'menu',
  currentNight: 1,
  difficulty: 'medium',
  isPaused: false,
  isLoading: false,
  timeElapsed: 0,
  nightDuration: 300, // 5 minutes default (will vary per night)
  objectives: [],
};

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    // Game state changes
    setGameState: (state, action: PayloadAction<GameState>) => {
      state.gameState = action.payload;
    },

    startGame: (state, action: PayloadAction<{ night: number; difficulty: Difficulty }>) => {
      state.gameState = 'playing';
      state.currentNight = action.payload.night;
      state.difficulty = action.payload.difficulty;
      state.timeElapsed = 0;
      state.isPaused = false;
    },

    pauseGame: (state) => {
      state.isPaused = true;
      state.gameState = 'paused';
    },

    resumeGame: (state) => {
      state.isPaused = false;
      state.gameState = 'playing';
    },

    gameOver: (state) => {
      state.gameState = 'gameOver';
      state.isPaused = true;
    },

    victory: (state) => {
      state.gameState = 'victory';
      state.isPaused = true;
    },

    returnToMenu: (state) => {
      state.gameState = 'menu';
      state.isPaused = false;
      state.timeElapsed = 0;
    },

    // Night management
    setCurrentNight: (state, action: PayloadAction<number>) => {
      state.currentNight = action.payload;
    },

    nextNight: (state) => {
      state.currentNight += 1;
      state.timeElapsed = 0;
    },

    // Difficulty
    setDifficulty: (state, action: PayloadAction<Difficulty>) => {
      state.difficulty = action.payload;
    },

    // Time management
    updateTimeElapsed: (state, action: PayloadAction<number>) => {
      state.timeElapsed = action.payload;
    },

    incrementTime: (state, action: PayloadAction<number>) => {
      state.timeElapsed += action.payload;
    },

    setNightDuration: (state, action: PayloadAction<number>) => {
      state.nightDuration = action.payload;
    },

    // Loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Objectives management
    setObjectives: (state, action: PayloadAction<ObjectiveData[]>) => {
      state.objectives = action.payload;
    },

    addObjective: (state, action: PayloadAction<ObjectiveData>) => {
      state.objectives.push(action.payload);
    },

    updateObjective: (state, action: PayloadAction<{ id: string; updates: Partial<ObjectiveData> }>) => {
      const objective = state.objectives.find(obj => obj.id === action.payload.id);
      if (objective) {
        Object.assign(objective, action.payload.updates);
      }
    },

    completeObjective: (state, action: PayloadAction<string>) => {
      const objective = state.objectives.find(obj => obj.id === action.payload);
      if (objective) {
        objective.completed = true;
      }
    },

    revealObjective: (state, action: PayloadAction<string>) => {
      const objective = state.objectives.find(obj => obj.id === action.payload);
      if (objective) {
        objective.hidden = false;
      }
    },

    clearObjectives: (state) => {
      state.objectives = [];
    },

    // Reset game
    resetGame: () => initialState,
  },
});

export const {
  setGameState,
  startGame,
  pauseGame,
  resumeGame,
  gameOver,
  victory,
  returnToMenu,
  setCurrentNight,
  nextNight,
  setDifficulty,
  updateTimeElapsed,
  incrementTime,
  setNightDuration,
  setLoading,
  setObjectives,
  addObjective,
  updateObjective,
  completeObjective,
  revealObjective,
  clearObjectives,
  resetGame,
} = gameSlice.actions;

export default gameSlice.reducer;
